import {
  ColorVisionDeficiencyType,
  ExamCardSectionNameType,
  ExamCardSectionType,
  gridTypeEnums,
} from "src/constants/enums";
import { PatientType } from "../patients/types";
import { BasicDeviceType } from "../devices/types";
import { DryEyeProfileType, IOLLensType } from "../clinics/types";

export type OptionalExamTogglesType = {
  autoPause?: boolean;
  calibration?: boolean;
  encouragement?: boolean;
  requiresTutorial?: boolean;
  unlimitedTime?: boolean;
  showProgressRing?: boolean;
};

export type ExamSectionType = {
  groupId: number;
  algorithm: string;
  completionDate: string | null;
  completionState: string;
  createdBy: string;
  creationDate: string;
  decibelValue?: number;
  eye: string;
  fixationLossMethodType?: string;
  fixationPointShapeType?: string;
  gridType?: keyof typeof gridTypeEnums;
  id: string;
  isDeleted: boolean;
  lastModifiedBy: string;
  lastModifiedDate: string;
  order: number;
  position?: string;
  stimulusSizeType?: string;
  stimulusColor?: string;
  timeLimit?: number;
  type: ExamCardSectionType;

  // lens simulation only
  lenses?: Array<IOLLensType>;
  profiles?: Array<DryEyeProfileType>;

  // videos only
  videoId?: string;
  videoName?: string;
  videoUrl?: string;
  videoVersion?: string;
} & OptionalExamTogglesType;

export type AddLensSectionType = {
  groupId: number;
  eye: string;
  algorithm: string;
  lensIds: Array<string>;
  profileIds: Array<string>;
  simulationEnvironmentType: string;
  order: number;
};

export type AddVideoSectionType = {
  groupId: number;
  eye: string;
  algorithm: string;
  videoId: string;
  videoName: string;
  videoVersion: string;
  videoUrl: string;
  order: number;
};

export type AddExamSectionType = {
  groupId: number;
  algorithm: string;
  decibelValue?: number | null;
  eye: string;
  binocular?: boolean;
  fixationLossMethodType?: string;
  fixationPointShapeType?: string;
  fovea?: boolean;
  gridType?: string;
  order: number;
  position?: string;
  startingRowIndex?: number;
  stimulusSizeType?: string;
  stimulusColor?: string;
  speedType?: string;
  timeLimit?: number;
  // Shared with aniseikonia, which spells it the same way and means something of its own by
  // it: whether the section aligns itself to the patient before measuring. On by default
  // there, so a section this card never mentions gets the alignment steps. There is no control
  // for it on the aniseikonia card and there is not meant to be -- turning it OFF is the
  // administrative act, done through Postman, and the card only sends back what the section
  // already had, because editing an exam deletes every section and re-adds it from what the
  // portal sends.
  objectiveAlignment?: boolean;
  subjectiveAngle?: boolean;
  cyclodeviation?: boolean;
  fusionalAmplitudes?: boolean;
  vergenceFunction?: boolean;
  stereoTargetStyle?: string;

  // aniseikonia only -- at least one meridian must be true, and `targetStyle` is the
  // webservice's spelling (the card holds it as `aniseikoniaTargetStyle`)
  horizontal?: boolean;
  vertical?: boolean;
  overall?: boolean;
  targetStyle?: string;
  settingsPerMeridian?: number;

  // lens simulation only
  lenses?: Array<IOLLensType>;
  lensIds?: Array<string>;
  profiles?: Array<DryEyeProfileType>;
  profileIds?: Array<string>;
  simulationEnvironmentType?: string;

  // videos only
  videoId?: string;
  videoName?: string;
  videoUrl?: string;
  videoVersion?: string;
} & OptionalExamTogglesType;

export type SelectedExamCardType = {
  groupId: number;
  examNumLabel?: number;
  examFieldType: string;
  examType: string;
  eyeCheckboxes: { right: boolean; left: boolean };
  decibelValue?: number | null;
  fixationType?: {
    fixationPointShapeType?: string;
    fixationLossMethod?: string;
  };
  sectionOrder?: { left: number; right: number };
  visualGrid?: string;
  speedType?: string;
  stimulusSizeType?: string;
  stimulusColor?: string;
  binocular?: boolean;
  position?: string;
  startingRowIndex?: number;
  fovea?: boolean;
  objectiveAlignment?: boolean;
  subjectiveAngle?: boolean;
  cyclodeviation?: boolean;
  fusionalAmplitudes?: boolean;
  vergenceFunction?: boolean;
  stereoTargetStyle?: string;

  // aniseikonia only
  horizontal?: boolean;
  vertical?: boolean;
  overall?: boolean;
  aniseikoniaTargetStyle?: string;
  aniseikoniaSettingsPerMeridian?: number;
  // Carried, never set. See the request type's note -- the card holds it only so an edit can
  // send back what the section already had. Spelled apart from the synoptophore
  // `objectiveAlignment` above because they are different sections' fields.
  aniseikoniaObjectiveAlignment?: boolean;

  optionalToggles: OptionalExamTogglesType;

  // lens simulation only
  profileIds?: Array<string>;
  lensIds?: Array<string>;
  simulationEnvironmentType?: string;

  // videos only
  videoId?: string;
  videoName?: string;
  videoUrl?: string;
  videoVersion?: string;
};

export type ExamType = {
  age: number;
  associatedDevice?: BasicDeviceType & {
    assignmentDate: string | null;
    id: string;
    unassignmentDate: string | null;
  };
  completionDate: string | null;
  completionState: string;
  createdBy: string;
  creationDate: string;
  id: string;
  isDeleted: boolean;
  lastModifiedBy: string;
  lastModifiedDate: string;
  nextSection: ExamSectionType;
  patient: PatientType;
  scheduledDate: string;
  type: string;
} & { [key in ExamCardSectionNameType]?: Array<ExamSectionType> };

export type ExternalExamDataType = {
  glaucomaHemifieldTest: string;
  visualFieldIndex: number;
  patternStandardDeviation: number;
  meanDeviation: number;
  falsePositive: number;
  falseNegative: number;
  fixationMethod: string;
  fixationValue: string;
  fixationLoss: number;
  machineSource: string;
  stimulusSize: string;
  foveaDb: string;
  notes: string;
  algorithmTypeReadable: string;
  algorithmType: string;
  gridType: keyof typeof gridTypeEnums;
  creationDate: string;
  createdBy: string;
  lastModifiedDate: string;
  lastModifiedBy: string;
  completionDate: string | null;
  isDeleted: boolean;
  patient: PatientType;
  age: number;
  patternDefect?: number;
  overallDefect?: number;
  clusterAnalysis?: string;
};

export type PositionalNoteType = {
  id: string;
  xPct: number;
  yPct: number;
  text: string;
};

export type IndividualExternalExamType = {
  id: string;
  clinicId: string;
  eye: string;
  date: string;
  examType: string;
  sourcePlatform: string;
  examThumbnailUrl: string | null;
  examFileUrl: string | null;
  images: Array<{
    id: string;
    imageUrl: string;
    notes: Array<PositionalNoteType>;
  }>;
  data: ExternalExamDataType;
};

export type ExternalExamType = {
  id: string;
  date: string;
  examThumbnailUrl: string;
  examType: string;
  eye: string;
  sourcePlatform: string;
  completionDate: string | null;
  isFavorite: boolean;

  data: ExternalExamDataType;
};

export type EnrichedProgressionAnalysisType = {
  createdBy: string;
  creationDate: string;
  exams: Array<EnrichedRetinaLogikSectionType>;
  externalExams: Array<EnrichedExternalExamType>;
  eye: string;
  id: string;
  lastModifiedBy: string;
  lastModifiedDate: string;
  state: string;
};

export type ProgressionAnalysisType = {
  createdBy: string;
  creationDate: string;
  exams: Array<Omit<ExamType, "patient">>;
  externalExams: Array<Omit<ExternalExamType, "patient">>;
  eye: string;
  id: string;
  lastModifiedBy: string;
  lastModifiedDate: string;
  state: string;
  baselineExamIds: Array<string>;
  baselineExternalExamIds: Array<string>;
};

// A point on a trendline; x is the date as days since the Unix epoch.
export type ProgressionTrendlinePointType = {
  date: string;
  x: number;
  y: number;
};

export type ProgressionTrendlineType = {
  title: string;
  metric: string;
  unit: string;
  fit_type: string;
  baseline_included: boolean;
  n: number;
  fit: {
    slope_per_day: number;
    slope_per_year: number;
    // the slope in whatever period suits the span of the exams, e.g. "month"
    rate_period?: string;
    slope_per_period?: number;
    // the metric's unit over that period, e.g. "dB/month"
    slope_per_period_unit?: string;
    intercept: number;
    // null until there are enough points for a p-value and confidence interval
    p_value: number | null;
    slope_ci_95_low: number | null;
    slope_ci_95_high: number | null;
    slope_ci_95_low_per_period?: number | null;
    slope_ci_95_high_per_period?: number | null;
  };
  series: Array<ProgressionTrendlinePointType & { isUnreliable: boolean }>;
  line: {
    start: ProgressionTrendlinePointType;
    end: ProgressionTrendlinePointType;
  };
};

export type ProgressionTrendlineVisitType = {
  visit_index: number;
  visit_date: string;
  examIds: Array<string>;
  isUnreliable: boolean;
  trendlines: Partial<Record<"MD" | "PSD" | "VRFI", ProgressionTrendlineType>>;
};

// Response of the progression analysis trendlines endpoint, keyed by eye
export type ProgressionAnalysisTrendlinesType = {
  analyses: Partial<
    Record<"Left" | "Right", { visits: Array<ProgressionTrendlineVisitType> }>
  >;
};

// for progression analysis
export type RetinaLogikExamSectionType = ExamSectionType & {
  examId: string;
  patientId: string;
  patientAge: number;
  isRetinaLogikExam: boolean;
};

// for progression analysis
export type ExternalExamSectionType = ExternalExamType & {
  isRetinaLogikExam: boolean;
};

// One row of the progression analysis picker, whatever hardware produced it.
// The backend filters and shapes these; `source` is the only thing that differs
// between an exam taken on our device and one imported from another machine.
export type ProgressionExamSummaryType = {
  id: string;
  examId: string | null;
  patientId: string;
  eye: string;
  gridType: keyof typeof gridTypeEnums;
  algorithm: string;
  completionDate: string;
  patientAge: number;
  source: string;
};

// A picker row after its measurements have been fetched by id on selection.
export type SelectedProgressionExamType = ProgressionExamSummaryType & {
  reportData?: ExamSectionReportType;
  data?: ExternalExamDataType;
  charts?: Array<ExternalExamChartType>;
};

export type AccessInfoType = {
  examId: string;
  deviceId: string | null;
  clinicId: string | null;
  fileName: string;
  access: {
    provider: string;
    containerName: string;
    accountName: string;
    sasKey: string;
    url: string;
  };
  recommendedFileName: string;
  reportType: string;
};

export type ReportInfoType = {
  id: string;
  exam?: {
    id: string;
    type: string;
    creationDate: string;
    createdBy: string;
    lastModifiedDate: string;
    lastModifiedBy: string;
    isDeleted: boolean;
    scheduledDate: string;
    completionDate: string;
    completionState: string;
  };
  progressionAnalysis?: {
    creationDate: string;
    createdBy: string;
    exams: Array<ExamType>;
    externalExams: Array<ExternalExamType>;
    eye: string;
    id: string;
    lastModifiedDate: string;
    lastModifiedBy: string;
    state: string;
  };
  type: string;
  status: string;
  downloadStatus: string;
  generationRequestId: string;
  lastModifiedDate: string;
};

export type ExamCommentsType = {
  id: string;
  content: string;
  conditions: Array<string>;
  otherConditions: null;
  creationDate: string;
  createdBy: string;
  lastModifiedDate: string;
  lastModifiedBy: string;
  isDeleted: boolean;
};

export type VisualSpotType = {
  color: null | string;
  intensity: number;
  isCompleted: boolean;
  isSeen: boolean;
  position: { type: string; x: number; y: number; z: number };
  width: number | null;
};

export type ProcessedPointType = {
  AgeCorrectedValue: number;
  PValue: number;
  PatternDeviation: number;
  PatternDeviationPercentile: number;
  Spot: VisualSpotType;
  TotalDeviation: number;
  TotalDeviationPercentile: number;
};

export type VisualAcuityChartRowType = {
  index: number;
  letters: Array<{ State: string; Value: string }>;
  logMARVisualAcuityScale: string;
  size: number;
  snellenVisualAcuityScaleFeet: string;
  snellenVisualAcuityScaleMetres: string;
};

export type AnisocoriaResultsType = {
  brightLightLeft: number;
  brightLightRight: number;
  dimLightLeft: number;
  dimLightRight: number;
};

export type RAPDResultsType = {
  affectedEye: string;
  hasRAPD: boolean;
  ndFilterValue: number;
  rawLogUnits: number;
  severity: string;
};

export type EyeAlignmentType = {
  diploType?: string;
  phoriaType?: string;
  tropiaType?: string;
  suppressionType?: string;
  responses?: Array<{
    expectedResponse: string;
    questionType: string;
    responseData: string;
  }>;
  slideSet?: { leftEyeImage: string; rightEyeImage: string };
};

export type SimultaneousPerceptionType = {
  distanceHorizontalDeviationPrismDiopters: string;
  distanceHorizontalDeviationType: string;
  distanceVerticalDeviationPrismDiopters: string;
  distanceVerticalDeviationType: string;
  slideSet: { leftEyeImage: string; rightEyeImage: string };
  torsionDegrees: string;
  torsionType: string;
};

export type FusionType = {
  responses: Array<{
    questionType: string;
    responseData: string;
    expectedResponse: string | null;
  }>;
  slideSet: {
    leftEyeImage: string;
    rightEyeImage: string;
  };
};

export type StereopsisType = {
  responses: Array<{
    questionType: string;
    responseData: string;
    expectedResponse: string | null;
  }>;
  slideSet: { leftEyeImage: string; rightEyeImage: string };
};

export type ChartSpotType = {
  decibelValue: number;
  x: number;
  y: number;
  completed: boolean;
  seen?: boolean;
  current: boolean;
  valuePercentile?: number;
  neighboursDecibel?: {
    bottom?: number;
    bottomLeft?: number;
    bottomRight?: number;
    left?: number;
    right?: number;
    top?: number;
    topLeft?: number;
    topRight?: number;
  };
};

type PupilEventDataType = {
  brightnessDBLeft: number;
  brightnessDBRight: number;
  date: string;
  eventType: string;
  leftEyeOpen: boolean;
  pupilDiameterLeft: number;
  pupilDiameterRight: number;
  rightEyeOpen: boolean;
};

type FieldDBUpdatedEventType = {
  endDate: string;
  index: number;
  previousLeftDb: number;
  previousRightDb: number;
  startDate: string;
  updatedLeftDb: number;
  updatedRightDb: number;
};

export type IshiharaStateResponseType = {
  plate?: {
    expectedResponses: { [key in ColorVisionDeficiencyType]: string };
    plateImage: string;
    plateType: string;
  };
  response?: {
    expectedResponses: { [key in ColorVisionDeficiencyType]: string };
    responseData: string;
  };
  questionType: string;
  responseData?: string;
  expectedResponse?: string | null;
};

export type OcularMotilityPositionResultsType = {
  closedEyeSampleCount: number;
  date: string;
  focalPointPosition: string;
  holdDuration: number;
  horizontalMisalignment: number;
  leftEyeAngleX: number;
  leftEyeAngleY: number;
  leftEyeDeviation: number;
  leftEyeExcursion: number | null;
  leftEyeSpread: number;
  misalignment: number;
  misalignmentFromPrimary: number;
  rightEyeAngleX: number;
  rightEyeAngleY: number;
  rightEyeDeviation: number;
  rightEyeExcursion: number | null;
  rightEyeSpread: number;
  sampleCount: number;
  targetAngleX: number;
  targetAngleY: number;
  verticalMisalignment: number;
};

// Shared by hPatternResults and nineGazeResults -- both exams post-process to
// the exact same shape, just walking a different subset of compass positions.
export type OcularMotilityResultsType = {
  eyeImageCount: number;
  eyeImages: Array<{
    data: string;
    date: string;
    eyes: string;
    eyesOpenVerified: boolean;
    focalPointAngleX: number;
    focalPointAngleY: number;
    focalPointPosition: string;
    format: string;
    height: number;
    leftEyeOpen: boolean;
    rightEyeOpen: boolean;
    strategyType: string;
    width: number;
  }>;
  positionResults: Array<OcularMotilityPositionResultsType>;
  summary: {
    measuredPositionCount: number;
    primaryMisalignment: number;
    misalignmentRange: number;
    largestMisalignment: number;
    largestMisalignmentPosition: string;
    primaryDeviatingEye: string | null;
    primaryDeviationDifference: number;
  };
};

// One entry of ExamSectionReportType's state.lenses -- pulled out to a named
// type so IOLOptions/ProgressiveOptions can read a lens's current parameter
// values (e.g. halosType) to seed their local state on first render.
export type ExamLensType = {
  // shared
  apertureRadius: number;
  axis: number;
  backgroundType: string;
  bgApertureRadius: number;
  bgAxis: number;
  bgCylinder: number;
  bgFocalDistance: number;
  bgFov: number;
  currentlyBeingViewed: { leftEye: boolean; rightEye: boolean };
  cylinder: number;
  focalDistance: number;
  focalFarThreshold: number;
  focalNearThreshold: number;
  focalRange: number;
  fov: number;
  lensType: string;
  name: string;
  preferred: { leftEye: boolean; rightEye: boolean };
  price: string;
  productType: string;
  viewedDates: Array<string>;

  // iol lenses
  reducedContrastType: string;
  starburstsType: string;
  glaresType: string;
  halosType: string;
  toricCylinderType: number | null;

  // progressive lenses
  bgTintColor: string | null;
  chromaticAberration: number;
  corridorWidth: number;
  corridorWidthType: string;
  depthDistort: number;
  depthDistortType: string;
  fittingHeight: number;
  corridorLengthType: string;
  indexType: string;
  isBlueLightFilterEnabled: boolean;
  isTintEnabled: boolean;
  photoChromicIntensity: number;
  powerProg: number;
  supportsBlueLightFilter: boolean;
  tintColor: string | null;
  tintDensityType: string;
  tintType: string;
};

export type DegreeDiopterType = {
  value: number;
  ci95: Array<number> | null;
  significant: boolean;
};

export type DeviationType = {
  horizontal: {
    degrees: DegreeDiopterType;
    direction: string;
    prismDioptres: DegreeDiopterType;
    thresholdDegrees: number;
  };
  periodCount: number;
  vertical: {
    degrees: DegreeDiopterType;
    direction: string;
    prismDioptres: DegreeDiopterType;
    thresholdDegrees: number;
  };
  windowsRejected: number;
  windowsUsed: number;
};

export type ReliabilityType = {
  blinkPercent: number;
  validSamplePercent: number;
  windowsRejected: number;
  windowsTotal: number;
};

// Every synoptophore module payload carries these, even one that didn't complete -- an aborted
// run stops at them, so everything past them is optional on the module types below.
export type SynoptophoreModuleStatusType = {
  durationSeconds: number;
  reliability: ReliabilityType | null;
  status: string;
  statusReason: string | null;
};

// Modules measured at both distances carry a whole payload (or summary) for each. Either side is
// null when that distance has nothing to report, e.g. a summary for a run that was aborted.
export type SynoptophoreDistancesType<T> = {
  far: T | null;
  near: T | null;
};

export type ObjectiveAlignmentModuleType = SynoptophoreModuleStatusType & {
  consistency?: {
    grade: string;
    manifestAgreesWithTotal: boolean | null;
    refixationCorroborated: boolean;
  };
  deviations?: {
    leftFixing: DeviationType;
    manifest: DeviationType;
    rightFixing: DeviationType;
    total: DeviationType;
  };
  fixationPreference?: string | null;
  fixationStability?: {
    leftBcea: DegreeDiopterType;
    nystagmusSuspected: boolean;
    rightBcea: DegreeDiopterType;
    thresholdDegreesSquared: number;
    vergenceBcea: DegreeDiopterType;
  };
  horizontal?: {
    direction: string;
    latentDegrees: DegreeDiopterType;
    latentPrismDioptres: DegreeDiopterType;
    manifestFraction: number;
    pattern: string;
    thresholdDegrees: number;
  };
  laterality?: string;
  recovery?: {
    grade: string;
    windows: Array<{
      brokeDown: boolean;
      coveredEye: string;
      recovered: boolean;
      stepId: string;
      timeSeconds: number | null;
    }>;
  };
  refixations?: Array<{
    detected: boolean;
    eye: string;
    horizontalShiftDegrees: number;
    stepId: string;
    verticalShiftDegrees: number;
  }>;
  vertical?: {
    direction: string;
    latentDegrees: DegreeDiopterType;
    latentPrismDioptres: DegreeDiopterType;
    manifestFraction: number;
    pattern: string;
    thresholdDegrees: number;
  };
};

export type SubjectiveAngleModuleType = SynoptophoreModuleStatusType & {
  angleOfAnomaly?: {
    horizontal: {
      degrees: DegreeDiopterType;
      prismDioptres: DegreeDiopterType;
      thresholdDegrees: number;
    };
    vertical: {
      degrees: DegreeDiopterType;
      prismDioptres: DegreeDiopterType;
      thresholdDegrees: number;
    };
  };
  correspondence?: string;
  fixatingEye?: string;
  objectiveAngle?: DeviationType;
  perTargetSize?: Array<{
    bothSeenUnderAlternateFlash: boolean;
    grade: string;
    horizontal: {
      ascendingDegrees: number;
      degrees: DegreeDiopterType;
      descendingDegrees: number;
      prismDioptres: DegreeDiopterType;
      spreadDegrees: number;
      thresholdDegrees: number;
    };
    neitherPictureSeen: boolean;
    simultaneousPerception: boolean;
    suppressedEye: string | null;
    targetSizeDegrees: number;
    trialsAttempted: number;
    trialsPerceived: number;
    trialsSet: number;
    vertical: {
      ascendingDegrees: number;
      degrees: DegreeDiopterType;
      descendingDegrees: number;
      prismDioptres: DegreeDiopterType;
      spreadDegrees: number;
      thresholdDegrees: number;
    };
  }>;
  referenceGrade?: string;
  suppression?: {
    perceptionFloor: string;
    recoveredByAlternateFlash: boolean | null;
    suppressingEye: string | null;
  };
};

export type CyclodeviationMeasurementType = {
  ascendingDegrees: number;
  ci95: Array<number> | null;
  descendingDegrees: number;
  direction: string;
  significant: boolean;
  spreadDegrees: number;
  thresholdDegrees: number;
  trialsAttempted: number;
  trialsPlaced: number;
  value: number;
};

export type CyclodeviationModuleType = SynoptophoreModuleStatusType & {
  attribution?: {
    agreementDegrees: number | null;
    consistent: boolean | null;
    monocularSumDegrees: number | null;
    thresholdDegrees: number | null;
  };
  fixatingEye?: string;
  gazeDependence?: Array<{
    gaze: string;
    total: CyclodeviationMeasurementType;
  }>;
  leftEye?: CyclodeviationMeasurementType;
  pattern?: string;
  placement?: {
    binocularTrials: number;
    binocularTrialsUnconfirmed: number;
    horizontalDegrees: number;
    source: string;
    verticalDegrees: number;
  } | null;
  rightEye?: CyclodeviationMeasurementType;
  total?: CyclodeviationMeasurementType;
};

export type FusionalAmplitudesModuleType = SynoptophoreModuleStatusType & {
  fixatingEye?: string;
  flags?: {
    prematureReport: boolean;
    suppressionMaskedABreak: boolean;
  };
  fusion?: {
    check: {
      centralSuppressionOnly: boolean;
      controlsMissing: string;
      controlsSeen: string;
      fused: boolean;
      simultaneousPerception: boolean;
      singleImageReported: boolean;
      stepId: string;
      suppressedEye: string | null;
    };
    present: boolean;
    quality: string;
    suppressedEye: string | null;
  };
  rampsRun?: boolean;
  ranges?: Array<{
    direction: string;
    valueDegrees: number;
    valuePrismDioptres: number | null;
    isLowerBound: boolean;
    breakDegrees: number | null;
    recoveryDegrees: number | null;
    hysteresisDegrees: number | null;
    objectiveBreakDegrees: number | null;
    agreement: string;
    recoveryExceedsBreak: boolean;
    suppressionMaskedBreak: boolean;
    controlsAtBreak: string;
    timeToBreakSeconds: number | null;
    rampRateDegreesPerSecond: number;
    measuredRampRateDegreesPerSecond?: number;
  }>;
  totals?: {
    cyclofusion: {
      degrees: number | null;
      isLowerBound: boolean;
      prismDioptres: number | null;
    };
    horizontal: {
      degrees: number | null;
      isLowerBound: boolean;
      prismDioptres: number | null;
    };
    vertical: {
      degrees: number | null;
      isLowerBound: boolean;
      prismDioptres: number | null;
    };
  };
};

// One deviation axis as the summary restates it: the prism value, the direction it was read in,
// and whether it cleared the module's threshold.
export type SynoptophoreAxisSummaryType = {
  direction: string | null;
  prismDioptres: number | null;
  significant: boolean;
};

export type ObjectiveAlignmentSummaryType = {
  cautions: Array<string>;
  horizontal: SynoptophoreAxisSummaryType & { pattern: string };
  laterality: string | null;
  leftFixing: {
    horizontal: SynoptophoreAxisSummaryType;
    vertical: SynoptophoreAxisSummaryType;
  };
  rightFixing: {
    horizontal: SynoptophoreAxisSummaryType;
    vertical: SynoptophoreAxisSummaryType;
  };
  vertical: SynoptophoreAxisSummaryType & { pattern: string };
};

export type SubjectiveAngleSummaryType = {
  angleOfAnomalyPrismDioptres: number | null;
  cautions: Array<string>;
  correspondence: string | null;
  horizontal: SynoptophoreAxisSummaryType;
  perceptionFloor: string | null;
  suppressingEye: string | null;
  vertical: SynoptophoreAxisSummaryType;
};

export type FusionalRangeSummaryType = {
  breakPrismDioptres: number | null;
  isLowerBound: boolean;
  recoveryPrismDioptres: number | null;
};

export type FusionalAmplitudesSummaryType = {
  cautions: Array<string>;
  convergence: FusionalRangeSummaryType | null;
  divergence: FusionalRangeSummaryType | null;
  fusionPresent: boolean;
  fusionQuality: string;
  suppressedEye: string | null;
  verticalTotal: {
    isLowerBound: boolean;
    prismDioptres: number | null;
  };
};

// Where one eye's half-picture sits on its screen, in degrees from straight ahead. Positive
// horizontal is to the patient's right, positive vertical is up.
export type SynoptophoreEyePlacementType = {
  horizontalDegrees: number;
  verticalDegrees: number;
  torsionDegrees: number;
  visible: boolean;
};

// What the headset is showing right now, sent with each live update. Null between steps.
export type SynoptophoreCurrentStepType = {
  module: string;
  activity: string;
  title: string;
  titleValues: Record<string, number | string>;
  viewingDistance: "Far" | "Near" | null;
  stepNumber: number | null;
  stepCount: number | null;
  iterationKind: string | null;
  iteration: number | null;
  iterationCount: number | null;
  field: {
    targetId: string;
    angularSizeDegrees: number;
    left: SynoptophoreEyePlacementType;
    right: SynoptophoreEyePlacementType;
    drivenEye: "Left" | "Right" | null;
    occludedEye: "Left" | "Right" | null;
    showSurround: boolean;
    superimpositionHorizontalDegrees: number;
    superimpositionVerticalDegrees: number;
    fixatingEye: "Left" | "Right" | null;
    alternateFlashPeriodSeconds: number | null;
    seed: number | null;
  };
  // Base64 PNGs of each eye's slide, spanning imageFieldDegrees. Null for pictorial slides.
  leftEyeImage: string | null;
  rightEyeImage: string | null;
  imageFieldDegrees: number | null;
  stereoTrial: {
    kind: string;
    targetFamily: string;
    disparityArcsec: number;
    seed: number;
    levelNumber: number;
    candidates: Array<string>;
    style: string;
    shapes: Array<string> | null;
    // Index into candidates of the one with depth; null on a catch trial
    disparateCandidate: number | null;
  } | null;
};

// ---------------------------------------------------------------------------------------------
// Aniseikonia
//
// Every length below is in DEGREES OF VISUAL ANGLE, and `fieldExtentDegrees` is HALF the width of
// the square field -- a canvas that maps it to half its own width gets every other number in the
// same units for free.
// ---------------------------------------------------------------------------------------------

// One eye's half of the target, exactly as the renderer was given it: the square-root split and
// the control's sign are already applied, so this never has to be derived from a percentage.
export type AniseikoniaHalfViewType = {
  widthDegrees: number;
  heightDegrees: number;
  offsetXDegrees: number;
  offsetYDegrees: number;
  // The side the straight edge sits on, so the two halves present their flat edges to each other.
  flatEdgeOnRight: boolean;
};

export type AniseikoniaViewType = {
  // Where the control sat. NOT the patient's aniseikonia -- it has the opposite sign.
  controlPercent: number;
  left: AniseikoniaHalfViewType;
  right: AniseikoniaHalfViewType;
};

// The invariant part of the picture, carried once per section.
export type AniseikoniaTargetGeometryType = {
  style: string;
  // The shape to draw: "HalfDisc" or "Bar".
  primitive: string;
  fieldExtentDegrees: number;
  eccentricityDegrees: number;
  lockInnerRadiusDegrees: number;
  lockOuterRadiusDegrees: number;
  lockRingCount: number;
  lockSectorCount: number;
  // The central fixation cross: the second fusion aid, at the centre of the field. One arm is
  // `fixationArmDegrees` long and `fixationThicknessDegrees` wide, the other is the transpose.
  // Absent on a report from a headset built before it existed, so draw it only when it is there.
  fixationArmDegrees?: number;
  fixationThicknessDegrees?: number;
  luminanceFraction: number;
  flatEdgeFacesCentre: boolean;
  halfScaleRule: string;
  // Per stage, because a bar is slender across the axis it is not judged on: with a bar pair the
  // unscaled feature is 2.0 x 0.7 horizontally and 0.7 x 2.0 vertically. Read the stage you draw.
  meridians: {
    [key in AniseikoniaMeridianKeyType]: {
      drivenAxis: string;
      featureWidthDegrees: number;
      featureHeightDegrees: number;
    };
  };
};

export type AniseikoniaMeridianKeyType = "horizontal" | "vertical" | "overall";

export type AniseikoniaSettingType = {
  stepId: string;
  // The patient's aniseikonia at this setting. Positive means the RIGHT eye sees the larger image.
  percent: number;
  controlPercent: number;
  startPercent: number;
  approach: string;
  referenceEye: string;
  outcome: string;
  durationSeconds: number;
  reversals: number;
  // null when the presentation produced no setting.
  view: AniseikoniaViewType | null;
};

export type AniseikoniaProbeType = {
  stepId: string;
  // "Zero" or "Offset". Never show this: a probe the technician can pick out is not a control.
  kind: string;
  presentedPercent: number;
  expectedPercent: number;
  settingPercent: number | null;
  errorPercent: number | null;
  withinTolerance: boolean;
  referenceEye: string;
  outcome: string;
  durationSeconds: number;
  // The difference the probe was STARTED with.
  presentedView: AniseikoniaViewType | null;
  view: AniseikoniaViewType | null;
};

export type AniseikoniaMeridianResultType = {
  meridian: string;
  // True on an overall meridian taken as the mean of the other two rather than measured.
  derived: boolean;
  percent: number;
  roundedPercent: number;
  // The range the settings fell in. NOT a 95% interval -- four settings do not support one.
  ciLow: number | null;
  ciHigh: number | null;
  largerEye: string;
  significant: boolean;
  beyondFusionRange: boolean;
  settingCount: number;
  settingsAttempted: number;
  approachSpreadPercent: number | null;
  referenceEyeGroupDeltaPercent: number | null;
  // True when the two values above are the same contrast and neither can be attributed to its
  // own cause. A meridian ordered with two settings runs one approach direction with each
  // reference eye -- the crossing that keeps hysteresis and interocular asymmetry apart needs
  // four settings and has only two to arrange -- so the approach spread and the reference-eye
  // delta come out as the same subtraction. Both are still sent; this says they are one
  // finding. Absent on a report from before the flag existed.
  approachAndReferenceEyeConfounded?: boolean;
  // Null when the meridian was measured once, which is the default: nothing was compared
  // with anything, and that is a different statement from two settings that came out far
  // apart. Read as `false` the card would tell the clinician the patient's answers would
  // not settle, about a patient who was asked once.
  settingsAgree: boolean | null;
  settingsAgreeTolerancePercent: number;
  // null altogether on a derived meridian.
  settings: Array<AniseikoniaSettingType> | null;
  probes: Array<AniseikoniaProbeType> | null;
};

export type AniseikoniaModuleType = {
  status: string;
  statusReason: string | null;
  durationSeconds: number;
  reliability: {
    controlProbePassRate: number | null;
    controlProbesPassed: boolean;
    zeroProbeBiasPercent: number | null;
    fusionLossEvents: number;
    fixationLossRate: number | null;
    suppressionEvents: number;
    settingCount: number;
    approachSpreadPercent: number | null;
    referenceEyeGroupDeltaPercent: number | null;
    probes: {
      presented: number;
      answered: number;
      correct: number;
      passRate: number | null;
      meanError: number | null;
      validityPassed: boolean;
    } | null;
  } | null;
  targetStyle: string;
  meridiansRun: Array<string>;
  meridians: {
    [key in AniseikoniaMeridianKeyType]?: AniseikoniaMeridianResultType;
  };
  meridionalDifferencePercent: number | null;
  fusionCheck?: {
    confirmed: boolean;
    // "Measured", "Remeasured", "HorizontalOnly" or "Parallel".
    placementUsed: string;
  } | null;
};

export type AniseikoniaMeridianSummaryType = {
  percent: number;
  // [low, high] -- the range the settings fell in, null on a derived meridian.
  ci: [number, number] | null;
  largerEye: string;
  significant: boolean;
  settingsAgree: boolean | null;
};

export type AniseikoniaSummaryType = {
  horizontal?: AniseikoniaMeridianSummaryType | null;
  vertical?: AniseikoniaMeridianSummaryType | null;
  overall?: AniseikoniaMeridianSummaryType | null;
  meridionalDifferencePercent: number | null;
  cautions: Array<string>;
};

// What the patient is looking at and doing right now, for the live update. Live only: it describes
// a moment rather than a result, so it never reaches a finished report.
//
// Nothing here says whether the presentation is a setting or a control probe, by design. Two of
// every six are probes with a known right answer, and a probe the technician can pick out stops
// being a control. The step's kind, its approach direction and its start position each identified
// one on their own, so none of the three is sent.
export type AniseikoniaLiveStepType = {
  module: string;
  // "MatchingWidth", "MatchingHeight", "MatchingOverallSize" or "AniseikoniaFusionCheck". Each is
  // a translation key of the same name, so a display never has to build the line itself.
  activity: string;
  meridian: string;
  stepNumber: number | null;
  stepCount: number | null;
  referenceEye: string;
  // The eye whose half the patient drives. Carried so nothing has to hold the "the other one"
  // rule itself.
  drivenEye: string;
  // Where the control sits. NOT the patient's aniseikonia -- the two have opposite signs, because
  // the patient nulls their own difference by driving the screen to the opposite one.
  controlPercent: number;
  // The mean of this meridian's settings so far, already flipped into an aniseikonia. Null before
  // the first setting on this meridian.
  runningEstimatePercent: number | null;
  travelMinPercent: number;
  travelMaxPercent: number;
  // The two halves at the sizes they are on the screen right now. The same block every setting in
  // the report carries, so a figure drawn from it works in both places.
  view: AniseikoniaViewType | null;
  // False while the patient has lost the peripheral fusion lock. The section cannot measure
  // anything in that state, so it is the one status worth making loud.
  lockHeld: boolean;
  fusionLossEvents: number;
  suppressionEvents: number;
};

/**
 * Narrows `state.currentStep`, which both the Synoptophore and Aniseikonia sections report under.
 *
 * Keyed on `lockHeld` because it is the field the aniseikonia step exists to carry: the section
 * measures nothing at all without the peripheral fusion lock, so a step without it is not one.
 */
export const isAniseikoniaLiveStep = (
  step: SynoptophoreCurrentStepType | AniseikoniaLiveStepType | null | undefined
): step is AniseikoniaLiveStepType =>
  Boolean(step) && "lockHeld" in (step as AniseikoniaLiveStepType);

// A per-eye difference in angular magnification caused by where the pupils sat is, at the level of
// the patient's report, the same thing as the patient's own aniseikonia -- nothing downstream can
// separate them. That is why the fit ships beside the number and why it has to be displayed.
export type AniseikoniaInstrumentType = {
  interpupillaryDistanceMetres: number | null;
  interpupillaryDistanceMeasured: boolean;
  targetDistanceMetres: number | null;
  leftOffAxisMetres: number | null;
  rightOffAxisMetres: number | null;
  leftEyeReliefMetres: number | null;
  rightEyeReliefMetres: number | null;
  withinCharacterisedFitRange: boolean;
  zeroProbeBiasPercent: number | null;
};

export type ExamSectionReportType = {
  device: { id: string; name: string; type: string; version: string };
  exam: {
    algorithm: string;
    eye: string;
    timeLimit?: number;
    type: string;
    version: string;
    decibelValue?: number;
    speedType?: string;
    stimulusSize?: string;
    stimulusColor?: string;
    grid?: keyof typeof gridTypeEnums;
    position?: string;
    fixationPointShape?: string;
    // Synoptophore stereopsis: which stimulus the ladder was run with (e.g. TitmusCircles,
    // SimpleShapes). modules.StereoLadder.targetStyle carries the same value.
    stereoTargetStyle?: string;

    // Aniseikonia: which shape the halves were drawn as, which meridians were run, and the
    // invariant half of the picture the patient saw.
    aniseikoniaTargetStyle?: string;
    meridians?: Array<string>;
    settingsPerMeridian?: number;
    objectiveAlignment?: boolean;
    targetGeometry?: AniseikoniaTargetGeometryType;
  };
  misc: { eyeTracking?: Array<number> };
  postProcessing: {
    version: string;
    metadataVersion?: string;

    result?: string;
    foveaDb?: number;
    tutorialCompletionState?: string;

    // synoptophore properties
    calibration?: {
      qualityFloorMet: boolean;
      residualDegrees: number;
    };
    eyeModel?: {
      interpupillaryDistanceMeasured: boolean | null;
      interpupillaryDistanceMetres: number | null;
      targetDistanceMetres: number | null;
    };
    wasSkipped?: boolean;

    // aniseikonia properties
    instrument?: AniseikoniaInstrumentType;

    modules?: {
      Cyclodeviation?: CyclodeviationModuleType;
      ObjectiveAlignment?: SynoptophoreDistancesType<ObjectiveAlignmentModuleType>;
      SubjectiveAngle?: SynoptophoreDistancesType<SubjectiveAngleModuleType>;
      FusionalAmplitudes?: SynoptophoreDistancesType<FusionalAmplitudesModuleType>;
      VergenceFunction?: {
        aca: {
          interpupillaryDistanceCentimetres: number;
          method: string;
          nearMinusDistancePrismDioptres: number | null;
          nominalDemandDioptres: number | null;
          ratio: number | null;
          validity: string;
        };
        deviation: {
          distance: {
            horizontal: {
              degrees: DegreeDiopterType;
              direction: string;
              prismDioptres: DegreeDiopterType;
              thresholdDegrees: number;
            };
            periodCount: number;
            vertical: {
              degrees: DegreeDiopterType;
              direction: string;
              prismDioptres: DegreeDiopterType;
              thresholdDegrees: number;
            };
            windowsRejected: number;
            windowsUsed: number;
          };
          // Same shape as distance -- both are full Deviation objects so
          // the difference between them is only as good as they are.
          near: {
            horizontal: {
              degrees: DegreeDiopterType;
              direction: string;
              prismDioptres: DegreeDiopterType;
              thresholdDegrees: number;
            };
            periodCount: number;
            vertical: {
              degrees: DegreeDiopterType;
              direction: string;
              prismDioptres: DegreeDiopterType;
              thresholdDegrees: number;
            };
            windowsRejected: number;
            windowsUsed: number;
          } | null;
          nearTestDistanceCentimetres: number;
        };
        durationSeconds: number;
        facility: {
          baseIn: {
            failed: number;
            medianSecondsToClear: number;
            presented: number;
          };
          baseOut: {
            failed: number;
            medianSecondsToClear: number;
            presented: number;
          };
          cyclesCleared: number;
          cyclesPerMinute: number;
          durationSeconds: number;
          limitingDirection: string | null;
          stepPrismDioptres: {
            baseIn: number;
            baseOut: number;
          };
        };
        fixatingEye: string;
        nearPoint: {
          agreement: string;
          breakCentimetres: number;
          objectiveBreakCentimetres: number;
          reachedTheNose: boolean;
          recession: {
            centimetres: number;
            recedes: boolean;
            thresholdCentimetres: number;
          };
          recoveryCentimetres: number;
          recoveryGapCentimetres: number;
          trials: Array<{
            breakCentimetres: number;
            objectiveBreakCentimetres: number;
            reachedTheNose: boolean;
            recoveryCentimetres: number;
            stepId: string;
            trialIndex: number;
          }>;
          trialsAttempted: number;
        };
        reliability: ReliabilityType | null;
        status: string;
        statusReason: string | null;
      };
      DirectComparison?: AniseikoniaModuleType;
      StereoLadder?: {
        alignmentAngle: {
          horizontal: {
            degrees: DegreeDiopterType;
            direction: string;
            prismDioptres: DegreeDiopterType;
            thresholdDegrees: number;
          };
          periodCount: number;
          vertical: {
            degrees: DegreeDiopterType;
            direction: string;
            prismDioptres: DegreeDiopterType;
            thresholdDegrees: number;
          };
          windowsRejected: number;
          windowsUsed: number;
        } | null;
        catchTrials: {
          correct: number;
          presented: number;
          // Only present from algorithm 1.3.0 onward; older reports have the counts but no
          // per-trial breakdown.
          trials?: Array<{
            correct: boolean;
            response: string;
            responseTimeSeconds: number;
            stepId: string;
          }>;
          validityPassed: boolean;
        };
        durationSeconds: number;
        fixatingEye: string;
        floorReached: boolean;
        // Shape unconfirmed -- every sample seen so far carries this as null.
        fusionCheck?: unknown;
        grossDepthOnly: boolean;
        // Only present from algorithm 1.3.0 onward.
        ladderArcsec?: Array<number>;
        levels: Array<{
          disparityArcsec: number;
          // Only present from algorithm 1.3.0 onward.
          passed?: boolean;
          resolved?: boolean;
          seed: number;
          targetFamily: string;
          trials: Array<{
            correct: boolean;
            response: string;
            responseTimeSeconds: number;
          }>;
        }>;
        pixelsPerDegree: number;
        // How much larger than nominal the slide and its disparities were drawn; every disparity
        // in the report is the nominal one. Only present from algorithm 1.13.0 onward.
        presentationScale?: number;
        reliability: ReliabilityType | null;
        renderableFloorArcsec: number;
        status: string;
        statusReason: string | null;
        targetStyle?: string;
        thresholdArcsec: DegreeDiopterType;
        // The full run in presentation order (rung and catch trials interleaved). Only present
        // from algorithm 1.3.0 onward -- levels[].trials is the only per-trial source before that.
        trials?: Array<{
          candidates: Array<string>;
          correct: boolean;
          disparityArcsec: number;
          expected: string;
          kind: string;
          // 1-based rung the trial belongs to; a catch trial carries the rung it was shown within.
          levelNumber?: number;
          order: number;
          response: string;
          responseTimeSeconds: number;
          seed: number;
          // The shape at each candidate position, in candidates order. Only set for the
          // SimpleShapes target style; null for TitmusCircles.
          shapes?: Array<string> | null;
          stepId: string;
          targetFamily: string;
        }>;
      };
    };

    summary?: {
      cautions: Array<string>;
      incompleteModules: Array<string>;
      Cyclodeviation?: {
        cautions: Array<string>;
        leftEye: { degrees: number | null; direction: string | null };
        rightEye: { degrees: number | null; direction: string | null };
        total: { degrees: number | null; direction: string | null };
      } | null;
      FusionalAmplitudes?: SynoptophoreDistancesType<FusionalAmplitudesSummaryType> | null;
      ObjectiveAlignment?: SynoptophoreDistancesType<ObjectiveAlignmentSummaryType> | null;
      // Aniseikonia. The technician view: everything a headline needs is here and nothing has
      // to be computed. Positive means the RIGHT eye sees the larger image, and `largerEye` is
      // carried so nothing has to derive it from the sign.
      overallPercent?: number;
      overallLargerEye?: string;
      overallSignificant?: boolean;
      meridionalDifferencePercent?: number | null;
      DirectComparison?: AniseikoniaSummaryType | null;

      StereoLadder?: {
        atOrBelow: boolean;
        cautions: Array<string>;
        interpretation: string;
        thresholdArcseconds: number | null;
      } | null;
      SubjectiveAngle?: SynoptophoreDistancesType<SubjectiveAngleSummaryType> | null;
      VergenceFunction?: {
        aca: {
          ratio: number | null;
          validity: string;
        } | null;
        cautions: Array<string>;
        distanceDeviation: {
          horizontal: SynoptophoreAxisSummaryType;
          vertical: SynoptophoreAxisSummaryType;
        } | null;
        facility: {
          cyclesPerMinute: number | null;
          limitingDirection: string | null;
        } | null;
        nearDeviation: {
          horizontal: SynoptophoreAxisSummaryType;
          vertical: SynoptophoreAxisSummaryType;
        } | null;
        nearPoint: {
          breakCentimetres: number | null;
          reachedTheNose: boolean;
          recedes: boolean;
          recoveryCentimetres: number | null;
        } | null;
      } | null;
    };

    // lens simulation properties
    preferredLensType?: string;

    // ocular motility properties
    hPatternResults?: OcularMotilityResultsType;

    nineGazeResults?: OcularMotilityResultsType;

    // color vision properties
    colorVisionDeficiencyType?: string;
    responseDistribution?: { [key in ColorVisionDeficiencyType]: string };

    // visual field properties
    processedPoints?: Array<ProcessedPointType>;
    patternStandardDeviation?: number;
    totalMeanDeviation?: number;
    totalMeanDeviationPValue?: string | number;
    retinaLogikFieldIndex?: number;
    glaucomaHemifieldTestIndex?: string;
    neuroFlagging?: { flag: string; comment: string };

    // RAPD properties
    anisocoriaResults?: AnisocoriaResultsType;
    blinkEvents?: Array<{ startDate: string; endDate: string }>;
    fieldDBUpdatedEvents?: Array<FieldDBUpdatedEventType>;
    pupilEventData?: Array<PupilEventDataType>;
    rapdResults?: RAPDResultsType;

    // visual acuity properties
    processedCharts?: Array<{
      chartIndex: number;
      result: string;
      rowIndex: number;
    }>;
  };
  state: {
    // video properties
    videoData?: {
      videoId: string;
      videoName: string;
      videoUrl: string;
      videoVersion: string;
    };

    // lens simulation properties
    lenses?: Array<ExamLensType>;
    simulationEnvironment?: string;

    // synoptophore properties
    activeModule?: string;
    completedModules?: Array<string>;
    // Both the Synoptophore and Aniseikonia sections report their live step under this
    // one key, as `state.currentStep`. Narrow on the section before reading it.
    currentStep?: SynoptophoreCurrentStepType | AniseikoniaLiveStepType | null;

    // ocular motility properties
    strategyQueue?: Array<string>;
    currentStrategy?: string;
    eyeData?: {
      strategyType: string;
      date: string;
      focalPointAngleX: number;
      focalPointAngleY: number;
      leftEyeAngleX: number;
      leftEyeAngleY: number;
      rightEyeAngleX: number;
      rightEyeAngleY: number;
      leftEyeOpen: boolean;
      rightEyeOpen: boolean;
    };
    focalPointPositions?: Array<{ Item1: number; Item2: number }>;
    gazePositionResults?: Array<OcularMotilityPositionResultsType>;

    // color vision properties
    colors?: Array<string>;
    row1?: Array<number>;
    row2?: Array<number>;

    // ishihara properties
    responses?: Array<IshiharaStateResponseType>;

    // tumbling e properties
    directions?: Array<{
      Index: number;
      color: string;
      letters: Array<{ Value: string; State: string }>;
    }>;

    // pelli robson properties
    letters?: Array<{
      Index: number;
      color: string;
      letters: Array<{ Value: string; State: string }>;
    }>;

    // visual acuity properties
    charts?: Array<{
      contrast: number;
      index: number;
      rows: Array<VisualAcuityChartRowType>;
    }>;
    letterIndex?: number;
    rowIndex?: number;

    // visual field properties
    visualSpots?: Array<VisualSpotType>;
    currentVisualSpot?: null | {
      intensity: number;
      position: { x: number; y: number };
      isCompleted: boolean;
      isSeen: boolean;
    };

    // ishihara properties
    currentQuestion?: {
      questionType: string;
      options: Array<string>;
      plate?: IshiharaStateResponseType["plate"];
    };

    // rapd properties
    pupilEventData?: Array<PupilEventDataType>;
    blinkEvents?: Array<{ startDate: string; endDate: string }>;
    fieldDBUpdatedEvents?: Array<FieldDBUpdatedEventType>;
  };
  stats: {
    progress: number;

    allSpotsCount?: number;
    averageResponseTime?: number;
    completedSpotsCount?: number;
    falseNegativePercentage?: string | number;
    falsePositivePercentage?: number;
    falseNegativeMisses?: number;
    falseNegativeCount?: number;
    fixationLossMethod?: string;
    fixationLossNonFixatedCount?: number;
    fixationLossTotalCount?: number;
    fixationLossPercentage?: string | number;
  };
  timing: {
    start: string;
    end: string;
    elapsed: number;
  };
  Progress?: number; // for live update data
};

export type ExamReportSectionType = {
  groupId: number;
  sectionName: ExamCardSectionNameType;

  display: boolean;

  commentData?: Array<ExamCommentsType>;
  duration?: string;
  durationStartTime?: string;
  examReport?: ExamSectionReportType;
  eyeType?: string;
  sectionId?: string;

  commentData2?: Array<ExamCommentsType>;
  duration2?: string;
  durationStartTime2?: string;
  examReport2?: ExamSectionReportType;
  eyeType2?: string;
  sectionId2?: string;
};

export type ExamReportDataType = {
  [key in ExamCardSectionNameType]?: {
    [groupId: number]: ExamReportSectionType;
  };
};

export type EnrichedRetinaLogikSectionType = RetinaLogikExamSectionType & {
  reportData: ExamSectionReportType;
};

export type ExternalExamChartType = {
  id: string;
  chartKind: string;
  imageUrl: string;
};

export type EnrichedExternalExamType = Omit<ExternalExamType, "patient"> & {
  isRetinaLogikExam: boolean;
  charts?: Array<ExternalExamChartType>;
};

export type ExamStatusType =
  | undefined
  | {
      status?: string;
      timelineStage?: number;
      extraWord?: string;
    };

export type GPAProcessInfoType = {
  id: string;
  lastModifiedDate: string;
  processRequestId: string;
  progressionAnalysis: ProgressionAnalysisType;
  status: string;
};

export type GPAChartPointType = {
  x: number;
  y: number;
  symbol: string;
  kind: string;
};

export type GPAChartLegendType = {
  dot: string;
  triangle1: string;
  triangle2: string;
  triangle3: string;
  deep: string;
  skip: string;
  blindspot: string;
};

// One comparison-against-baseline chart per follow-up exam (current format).
export type GPAVisitType = {
  visit_index: number;
  visit_date: string;
  status: string;
  examIds?: Array<string>;
  gpa_chart: {
    points: Array<GPAChartPointType>;
    legend: GPAChartLegendType;
  };
};

// Single-snapshot format (only reflects the latest exam) written by older,
// already-stored GPA blobs, kept for backward compatibility.
export type GPALegacyChartType = {
  algo: string;
  confidence_mean_percent: number;
  legend: GPAChartLegendType;
  mode: string;
  note: string;
  points: Array<GPAChartPointType>;
  status: string;
  test_date: string;
  tier: string;
};

// The two exams GPA measures every follow-up against. They have no chart of
// their own (they ARE the reference), so exam rows matching these are labelled
// rather than left with an unexplained empty GPA column.
export type GPABaselineType = {
  indices?: Array<number>;
  dates?: Array<string>;
  examIds?: Array<string>;
};

// Why an exam row has no GPA chart of its own: it is one of the two baseline
// exams, or GPA collapsed it as a same-day repeat (it keeps one test per day).
export type GPAExclusionReason = "baseline" | "superseded";

export type GPADataAnalysisType = {
  baseline?: GPABaselineType;
  gpa_chart?: GPALegacyChartType;
  visits?: Array<GPAVisitType>;
  note?: string;
};

export type GPADataType = {
  analyses: {
    Left?: GPADataAnalysisType;
    Right?: GPADataAnalysisType;
  };
};
